/**
 * Mapa symbol kontenera (DB `imp_cms_container.symbol`) -> React Container Component.
 *
 * Backend (PHP) zwraca `container_symbol` w response (`IAPI_windows` /
 * `IAPI_catalogByPath`) z 2-warstwowego lookup'u w SQL:
 *   1. V1 per-instance: `imp_cms_window.id_domain_component_container`
 *      lub `imp_cms_catalog.id_domain_component_container`
 *   2. V1 default: `imp_cms_domain_component_container.default='1'` per (domena, komponent)
 *
 * Adapter (`WindowRenderer` / `CatalogRenderer`) resolves symbol -> komponent
 * z tej mapy, przekazuje go jako `ContainerOverride` do dispatch lambda w
 * `windowVariants` / `catalogVariants`. Dispatch lambda ma fallback:
 *   `const Container = ContainerOverride ?? <hardcoded WEmpty/WDefault/CEmpty/CDefault>`
 *
 * **Precedencja (z perspektywy frontend):**
 *   1. React areaOverrides (`windowVariants.areaOverrides[symbol][area]`) -
 *      jeśli zdefiniowany, COMPLETE renderer (Container hardcoded)
 *   2. DB `container_symbol` (z tej mapy) - override dla `defaults[symbol]` renderera
 *   3. React hardcoded default (w `defaults[symbol]` lambda) - fallback gdy DB null
 *
 * **Aktualne 6 symboli** (po seedzie 2026-05-29 + pole `type` 2026-06-02):
 *   - type='1' (okno): `default`, `empty`, `clear`, `grey_title`
 *   - type='2' (katalog): `catalog_default`, `catalog_empty`
 *
 * **Dodawanie nowego kontenera:**
 *   1. Admin w IADMIN > System administracyjny > CMS > Kontenery > Dodaj.
 *      Symbol = `[a-z_]+`, typ '1' (okno) lub '2' (katalog).
 *   2. Tutaj: zarejestruj symbol -> React component w `CONTAINERS`.
 *   3. Symbol bez wpisu w tej mapie -> resolveContainer zwraca null -> dispatch
 *      lambda używa hardcoded fallback'u (np. `WEmpty`).
 */

import type { ComponentType, ReactNode } from "react";
import type { CmsBackground } from "@/lib/api/cms-windows";
import { WDefault } from "./WDefault";
import { WEmpty } from "./WEmpty";
import { CDefault } from "./CDefault";
import { CEmpty } from "./CEmpty";

/**
 * Wspólny interfejs Container component'u - akceptuje opcjonalny title (h1/h3),
 * opcjonalny background (CmsBackground struct z `m_site_backgrounds`),
 * i children. Wszystkie 4 reusable kontenery (WDefault/WEmpty/CDefault/CEmpty)
 * pasują do tego typu.
 */
export type ContainerComponent = ComponentType<{
  title?: string;
  /** Opcjonalny podtytul renderowany pod h1 w kontenerach ktore go obsluguja
   *  (CDefault). Kontenery bez naglowka (CEmpty/WEmpty) prop ignoruja. */
  subtitle?: string | null;
  background?: CmsBackground | null;
  children: ReactNode;
}>;

/**
 * Mapa `imp_cms_container.symbol` -> Container React component.
 * Niezarejestrowany symbol -> `resolveContainer` zwraca null (dispatch lambda
 * używa swojego hardcoded fallback'u).
 */
const CONTAINERS: Record<string, ContainerComponent> = {
  /* type='1' (okno) - 4 wpisy z seedu 2026-05-29 */
  default: WDefault,    // h3 + ramka (admin chce label instancji okna)
  empty: WEmpty,        // passthrough
  clear: WEmpty,        // passthrough (alias 'empty')
  grey_title: WDefault, // h3 + ramka (TODO: WGreyTitle dla custom styling)

  /* type='2' (katalog) - 2 wpisy z seedu */
  catalog_default: CDefault, // h1 + card (page title + background)
  catalog_empty: CEmpty,     // passthrough
};

/**
 * Resolve Container component po symbolu z DB.
 *
 * @param symbol Wartość `container_symbol` z backend response (lub null gdy
 *               brak ustawienia per-instance i brak default'a per komponent).
 * @return Container component lub null gdy symbol niezarejestrowany / null input.
 */
export function resolveContainer(
  symbol: string | null | undefined,
): ContainerComponent | null {
  if (!symbol) return null;
  return CONTAINERS[symbol] ?? null;
}

/**
 * Set zarejestrowanych symboli kontenerów - dla diagnostyki (np. AlignmentChecker
 * wykrywa kiedy backend zwraca symbol nieznany React-CMS).
 */
export const KNOWN_CONTAINER_SYMBOLS: ReadonlyArray<string> = Object.keys(CONTAINERS);
